import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from 'lucide-react';
import { showLargeToast } from '@/components/ui/sonner';
import { toast } from '@/components/ui/sonner';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message);
      setEmail(response.data.email);
      
      showLargeToast(
        'Welcome to ExpatsSphere!',
        'Your email has been verified. You\'re now officially a member of our global community!',
        'success'
      );
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error('Please enter your email');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification-public`, { email: resendEmail });
      setResent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-3xl border-2 border-border p-8 text-center shadow-xl">
          <Link to="/" className="inline-block mb-8">
            <h1 className="text-2xl font-heading font-bold">
              <span className="text-foreground">Expats</span>
              <span className="text-primary italic">Sphere</span>
            </h1>
          </Link>

          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Verifying your email...</h2>
              <p className="text-muted-foreground">
                Please wait while we verify your email address.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">Email Verified!</h2>
              <p className="text-muted-foreground mb-6">{message}</p>
              {email && (
                <p className="text-sm text-muted-foreground mb-6">
                  Verified email: <strong>{email}</strong>
                </p>
              )}
              
              {/* Detect if opened in browser (not the PWA) and show helpful message */}
              {typeof window !== 'undefined' && !window.navigator.standalone && !window.matchMedia('(display-mode: standalone)').matches && (
                <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-4 mb-4 text-left">
                  <p className="text-sm font-medium mb-1">Open the ExpatsSphere app to continue</p>
                  <p className="text-xs text-muted-foreground">
                    If you have the app installed, switch to it now. Your account is verified and ready to use.
                  </p>
                </div>
              )}
              
              <Button
                onClick={() => navigate(user ? '/onboarding' : '/')}
                className="w-full h-12 rounded-full font-bold"
                data-testid="verify-go-home-btn"
              >
                {user ? 'Complete Your Profile' : 'Sign In to Get Started'}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">Verification Failed</h2>
              <p className="text-muted-foreground mb-6">{message}</p>
              
              {!resent ? (
                <form onSubmit={handleResend} className="space-y-3 mb-4">
                  <p className="text-sm text-muted-foreground text-left">
                    Enter your email to receive a new verification link:
                  </p>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Your email address"
                    className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                    required
                    data-testid="resend-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={resending}
                    variant="outline"
                    className="w-full h-12 rounded-full"
                    data-testid="resend-verification-btn"
                  >
                    {resending ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" />Send New Verification Email</>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-2 text-green-600 py-3 mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <span>Verification email sent! Check your inbox.</span>
                </div>
              )}
              
              <Button
                onClick={() => navigate('/')}
                className="w-full h-12 rounded-full font-bold"
                data-testid="verify-error-go-home-btn"
              >
                Go to Homepage
              </Button>
            </>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Need help? Contact us at{' '}
          <a href="mailto:support@expatssphere.com" className="text-primary hover:underline">
            support@expatssphere.com
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
